import { ShopOrderCsMemoLogItem } from '@shared/api/shopOrderCsMemo/shopOrderCsMemo.interface';
import { deliveryStatusLabel, paymentStatusLabel } from '@shared/utils/orderStatusLabel';
import { orderBrandName } from '@shared/utils/bugoBrandName.util';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { bareReceiverName } from '@shared/utils/receiverName.util';
import {
  cancelProgress,
  deliveryDateInfo,
  refundRouteLabel,
} from '@shared/utils/shopOrderCsSummary';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

// 화환 CS 응대 로그 엑셀 — 응대 매뉴얼·교육자료용.
// 프로젝트 표준(커뮤니티 xlsx + file-saver)만 사용하고, 스타일 대신 너비·고정·필터로 읽히게 한다.

const p = (n: number) => String(n).padStart(2, '0');

const fmt = (date: Date | string | undefined) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(
    d.getHours(),
  )}:${p(d.getMinutes())}`;
};

const fmtD = (date: Date | string | undefined) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const exportShopCsMemoLogExcel = (
  items: ShopOrderCsMemoLogItem[],
  range: { from: string; to: string; orderNo?: string },
) => {
  const rows = items.map((item, index) => {
    const order = item.order;
    const cancel = cancelProgress(order ?? {});
    const delivery = deliveryDateInfo(order ?? {});
    return {
      //* 화면 표와 같은 규칙 — 최신 건이 가장 큰 번호 ("42번 건" 으로 서로 가리키기 위한 열)
      순번: items.length - index,
      작성일시: fmt(item.createdAt),
      작성자: item.byName,
      역할: item.byRole === 'Admin' ? '관리자' : '판매자',
      공개범위: item.adminOnly ? '관리자 전용' : '공유',
      //* 같은 주문에 달린 메모끼리 엑셀에서 정렬·묶어보게 하는 키 (화면 표기와 같은 규칙)
      주문번호: order?.orderNo ?? item.shopOrder.slice(-6),
      응대건수: order?.memoCount ?? '',
      보내는분: order?.senderName ?? '',
      받는분: bareReceiverName(order?.receiverName) ?? '',
      //* 상조 — 어느 상조 건에서 문의가 몰리는지 피벗의 1순위 축 (엑셀은 축약 없이 전체명)
      상조: orderBrandName(order),
      //* 배송지 — 어느 장례식장에서 문제가 반복되는지 피벗으로 보려고 항목을 나눠 담는다
      고인명: order?.deceasedName ?? '',
      장례식장: order?.funeralHomeName ?? '',
      호실: order?.roomName ?? '',
      배송주소: order?.receiverAddress ?? '',
      //* 배송완료 사진 — 퀄리티 문의는 실물을 봐야 답이 나온다 (원본 링크)
      사진: order?.deliveredImageUrl ? fileBucketUrl(order.deliveredImageUrl, 'raw') : '',
      //* 리본 문구 — 오기입 문의의 실제 대상이라 전문 그대로
      보내는문구: order?.senderPhrase ?? '',
      근조문구: order?.condolencePhrase ?? '',
      상품: order?.itemName ?? '',
      수량: order?.quantity ?? '',
      단가: order?.priceRetail ?? '',
      //* 금액은 합계·현금·포인트를 각각 열로. 합계만 두면 "환불 얼마" 계산이 안 된다
      //* (현금분은 PG 환불, 포인트분은 즉시 반환 — 백엔드 회신 §1-1)
      결제금액: order?.amount ?? '',
      현금결제: order?.paymentByCash ?? '',
      포인트결제: order?.paymentByPoint ?? '',
      상품교체: order?.hasItemChange ? 'Y' : '',
      금액조정경위: order?.paymentMemo ?? '',
      결제수단: order?.method ?? '',
      PG: order?.provider ?? '',
      가상계좌: order?.isVbank ? 'Y' : '',
      //* 환불 경로는 서버가 확정한 refundRoute 그대로 — method 문자열로 재판정하지 않는다
      환불경로: refundRouteLabel(order ?? {}) ?? '',
      //* 응대 매뉴얼·교육자료로 쓰이는 파일이라 개발용 원문(Paid/Delivered) 대신 운영 표기로 내보낸다
      결제상태: order?.paymentStatus ? paymentStatusLabel(order.paymentStatus) : '',
      배송상태: order?.deliveryStatus ? deliveryStatusLabel(order.deliveryStatus) : '',
      결제일: fmtD(order?.purchasedAt),
      배송일: delivery ? fmtD(delivery.date) : '',
      배송일구분: delivery ? (delivery.estimated ? '예정' : '완료') : '',
      //* canceledAt은 취소 "요청" 시각이라 원문 날짜만 담으면 오독된다 — 라벨을 같이 넣는다
      취소구분: cancel?.label ?? '',
      취소일: cancel ? fmtD(cancel.date) : '',
      취소사유: order?.cancelReason ?? '',
      내용: item.body,
    };
  });

  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = [
    { wch: 6 }, // 순번
    { wch: 18 }, // 작성일시
    { wch: 10 }, // 작성자
    { wch: 8 }, // 역할
    { wch: 12 }, // 공개범위
    { wch: 10 }, // 주문번호
    { wch: 8 }, // 응대건수
    { wch: 12 }, // 보내는분
    { wch: 12 }, // 받는분
    { wch: 14 }, // 상조
    { wch: 10 }, // 고인명
    { wch: 20 }, // 장례식장
    { wch: 10 }, // 호실
    { wch: 34 }, // 배송주소
    { wch: 40 }, // 사진
    { wch: 26 }, // 보내는문구
    { wch: 22 }, // 근조문구
    { wch: 24 }, // 상품
    { wch: 6 }, // 수량
    { wch: 10 }, // 단가
    { wch: 12 }, // 결제금액
    { wch: 12 }, // 현금결제
    { wch: 12 }, // 포인트결제
    { wch: 8 }, // 상품교체
    { wch: 40 }, // 금액조정경위
    { wch: 14 }, // 결제수단
    { wch: 12 }, // PG
    { wch: 8 }, // 가상계좌
    { wch: 24 }, // 환불경로
    { wch: 10 }, // 결제상태
    { wch: 10 }, // 배송상태
    { wch: 12 }, // 결제일
    { wch: 12 }, // 배송일
    { wch: 10 }, // 배송일구분
    { wch: 10 }, // 취소구분
    { wch: 12 }, // 취소일
    { wch: 24 }, // 취소사유
    { wch: 80 }, // 내용
  ];
  ws['!freeze'] = { xSplit: '0', ySplit: '1' };
  if (rows.length > 0) {
    //* 마지막 컬럼 인덱스는 !cols 길이에서 파생 — 컬럼을 늘릴 때 여기를 잊어 필터가 잘리는 일을 막는다
    const lastCol = ws['!cols'].length - 1;
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: rows.length, c: lastCol },
      }),
    };
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'CS응대로그');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  //* 특정 주문만 보던 중이면 기간이 의미 없으므로 파일명도 주문번호로 (기간 무시 조회였음)
  const suffix = range.orderNo ? `주문${range.orderNo}` : `${range.from}_${range.to}`;
  saveAs(
    new Blob([buf], { type: 'application/octet-stream' }),
    `화환CS응대로그_${suffix}.xlsx`,
  );
};
